import BaseAPI from './base.api';

export interface DispatchDeviceLatest {
  id: string;
  deviceId: string;
  operatorId: string;
  operatorName: string;
  sessionId: string;
  action: string;
  timestamp: number;
  details: string;
  payload: {
    materialDumped: number;
    completionPercentage: number;
    completedTrips: number;
    targetProduction: number;
    targetTrips: number;
    routeFrom: string;
    routeTo: string;
    nextFrom?: string;
    nextTo?: string;
    totalPayload: number;
    truckStatus: string;
    efficiency?: number;
    excavator?: string;
  };
  // root-level fields from SSE (PLAN_KPI_RECALCULATED)
  totalMaterialDumped?: number;
  currentDumped?: number;
  syncTimestamp: number;
}

export interface DispatchLogEntry {
  id: string;
  operatorId: string;
  operatorName: string;
  sessionId: string;
  action: string;
  timestamp: number;
  details: string;
  payload: Record<string, any> | null;
  syncTimestamp: number;
}

export interface DispatchDevice {
  deviceId: string;
  latest: DispatchDeviceLatest | null;
  data?: Array<DispatchLogEntry | null>;
}

export interface DispatchAssetStatusResponse {
  customerId: string;
  customerCode: string;
  devices: DispatchDevice[];
}

export interface LastStatusEntry {
  id: string;
  operatorId: string;
  operatorName: string;
  sessionId: string;
  action: string;
  timestamp: number;
  details: string;
  payload: Record<string, any> | null;
  syncTimestamp: number;
  device: string;
  asset?: string;
}

export interface LastStatusDevice {
  deviceId: string;
  last: LastStatusEntry | null;
}

export interface LastStatusResponse {
  customerId: string;
  customerCode: string;
  devices: LastStatusDevice[];
}

export interface MaterialBlendInfo {
  customerCode: string;
  customerId: string;
  locationId: string;
  locationName: string;
  qualities: Array<Record<string, number>>;
}

export interface LatestBlendEntry {
  id: string;
  operatorId: string;
  operatorName: string;
  asset: string;
  device: string;
  loadingPoint: string;
  dumpingZone: string;
  material: string;
  trips: number;
  totalMaterialDumped: number;
  currentDumped: number;
  MaterialBlend: MaterialBlendInfo | null;
  payload: {
    truckStatus: string;
    completedTrips: number;
    totalPayload: number;
    targetProduction: number;
    targetTrips: number;
    routeFrom: string;
    routeTo: string;
    efficiency?: number;
  };
}

export interface LatestBlendDumpingPoint {
  dumpingPointName: string;
  data: Record<string, LatestBlendEntry>;
}

export interface LatestBlendResponse {
  customerId: string;
  customerCode: string;
  dumpingPoints: LatestBlendDumpingPoint[];
}

export interface HourlyBlendEntry {
  hour: string;
  data: Record<string, LatestBlendEntry> | null;
}

export interface HourlyBlendDumpingPoint {
  dumpingPointName: string;
  hourly: HourlyBlendEntry[];
}

export interface HourlyBlendResponse {
  customerId: string;
  customerCode: string;
  date: string;
  dumpingPoints: HourlyBlendDumpingPoint[];
}

export interface OverridePlanPayload {
  device: string;
  overrideAction: string;
  newDestination: string;
  applyFor: string;
  reason?: string;
}

interface HaulerOverrideBase {
  haulTrucks: string[];
  haulTruckToOverride: string;
}

export interface HaulerTruckStatusChangePayload extends HaulerOverrideBase {
  overrideType: 'TRUCK_STATUS_CHANGE';
  statusCode: 'down' | 'delay';
  durationMinutes: number;
}

export interface HaulerCancelStatusChangePayload extends HaulerOverrideBase {
  overrideType: 'CANCEL_STATUS_CHANGE';
}

export interface HaulerAssignmentChangePayload extends HaulerOverrideBase {
  overrideType: 'ASSIGNMENT_CHANGE';
  assignmentTarget: 'current' | 'next';
  scope?: 'next_trip' | 'from_next_trip_onwards';
  node: string;
  nodeType: 'loading' | 'dumping' | 'parking';
}

export type HaulerOverridePayload =
  | HaulerTruckStatusChangePayload
  | HaulerCancelStatusChangePayload
  | HaulerAssignmentChangePayload;

export interface HaulerOverrideResponse {
  sessionId: string;
  step: { trucksUpdated: number; trucksSkipped: string[] };
  override?: { code: string; message?: string };
  removal?: { haulTruckToOverride: string; durationMinutes: number };
  cancellation?: { haulTruckToOverride: string };
}

export interface TripEntryPayload {
  planId?: string;
  duration?: number;
  routeFrom: string;
  routeTo: string;
  materialDumped?: number;
  excavator?: string;
  nextFrom?: string;
  nextTo?: string;
}

export interface TripEntry {
  id: string;
  operatorId: string;
  operatorName: string;
  sessionId: string;
  action: string;
  timestamp: number;
  details: string;
  type: string;
  location: string;
  payload: TripEntryPayload;
  syncTimestamp: number;
  device: string;
  asset: string;
  sensor_ts: number;
  name: string;
}

export interface GetTripsResponse {
  customerId: string;
  customerCode: string;
  trips: Record<string, TripEntry>;
}

export interface ProximityNearbyTruck {
  bumperNumber: string;
  distance_m: number;
  gps_velocity_value?: string;
  gps_angle_value?: string;
  gps_latitude_value?: number;
  gps_longitude_value?: number;
}

export interface ProximityEntry {
  bumperNumber: string;
  device_id: string;
  nearby_trucks: ProximityNearbyTruck[];
  truck_count: number;
  computed_at: number;
}

export interface CongestionEntry {
  locationId: string;
  name: string;
  locationType: string;
  truckCount: number;
  trucksCapacity: number;
  congested: boolean;
  trucks: string[];
  computedAt: number;
}

export interface GetLatestProximityCongestionResponse {
  customerId: string;
  customerCode: string;
  proximity: Record<string, ProximityEntry>;
  congestion: Record<string, CongestionEntry>;
}

export interface BlockedRoad {
  roadId: string;
  reason: string;
  blockedAt: string;
  ttlSeconds: number | null;
}

export interface GetBlockedRoadsResponse {
  customerId: string;
  customerCode: string;
  blockedRoads: BlockedRoad[];
}

export default class DispatchMonitorAPI extends BaseAPI {
  // Full URL for the live-dispatch-sse stream, built from the same base URL
  // as every other call (BaseAPI's axios baseURL). EventSource can't go
  // through axios, so the caller opens the stream, but the base/customerId
  // still come from here instead of reading process.env in the component.
  liveDispatchSSEUrl = (customerId: string): string => {
    const baseURL = this.axiosInstance.defaults.baseURL ?? '';
    return `${baseURL}dispatch-monitor/live-dispatch-sse?customerId=${customerId}`;
  };

  getLatestDispatchAssetStatus = async (
    tablogs = false
  ): Promise<DispatchAssetStatusResponse | null> => {
    const response = await this.API.get(
      'dispatch-monitor/get-latest-dispatch-asset-status',
      { params: { tablogs } }
    );
    return response.data;
  };

  getLastStatus = async (): Promise<LastStatusResponse | null> => {
    const response = await this.API.get('dispatch-monitor/get-last-status', {
      params: { customerId: this.customerId },
    });
    return response.data;
  };

  sendOverridePlan = async (payload: OverridePlanPayload): Promise<void> => {
    await this.API.post('dispatch-monitor/override-ride', payload);
  };

  getLatestBlend = async (): Promise<LatestBlendResponse> => {
    const response = await this.API.get('dispatch-monitor/get-latest-blend', {
      params: { customerId: this.customerId },
    });
    return response.data;
  };

  getHourlyBlend = async (
    customerId: string,
    date: string
  ): Promise<HourlyBlendResponse> => {
    const response = await this.API.get('dispatch-monitor/get-hourly-blend', {
      params: { customerId, date },
    });
    return response.data;
  };

  getAssetInfo = async (): Promise<{ assets: any[] }> => {
    const response = await this.API.get('dispatch-monitor/get-asset-info', {
      params: { customerId: this.customerId },
    });
    return response.data;
  };

  getAssetLastStatus = async (): Promise<{ assets: any[] }> => {
    const response = await this.API.get(
      'dispatch-monitor/get-asset-last-status',
      { params: { customerId: this.customerId } }
    );
    return response.data;
  };

  updateAssetStatus = async (
    assetId: string,
    status: string,
    reason?: string,
    description?: string
  ): Promise<void> => {
    await this.API.patch(
      'dispatch-monitor/update-asset-status',
      {
        status,
        ...(reason ? { reason } : {}),
        ...(description ? { description } : {}),
      },
      { params: { customerId: this.customerId, assetId } }
    );
  };

  getLatestDispatchSession = async (): Promise<{
    customerId: string;
    customerCode: string;
    session: string;
    ttl: number;
  } | null> => {
    const response = await this.API.get(
      'dispatch-monitor/get-latest-dispatch-session',
      { params: { customerId: this.customerId } }
    );
    return response.data;
  };

  getTrips = async (): Promise<GetTripsResponse | null> => {
    const response = await this.API.get('dispatch-monitor/get-trips', {
      params: { customerId: this.customerId },
    });
    return response.data;
  };

  getLatestProximityCongestion =
    async (): Promise<GetLatestProximityCongestionResponse | null> => {
      const response = await this.API.get(
        'dispatch-monitor/get-latest-proximity-congestion',
        { params: { customerId: this.customerId } }
      );
      return response.data;
    };

  overrideHaulerMonitor = async (
    payload: HaulerOverridePayload
  ): Promise<HaulerOverrideResponse> => {
    const response = await this.API.post(
      'dispatch-monitor/hauler-monitor/override',
      payload,
      { params: { customerId: this.customerId } }
    );
    return response.data;
  };

  blockRoad = async (
    roadId: string,
    reason: string,
    ttlMinutes?: number
  ): Promise<BlockedRoad> => {
    const response = await this.API.post(
      'dispatch-monitor/hauler-monitor/block-road',
      { roadId, reason, ...(ttlMinutes != null && { ttlMinutes }) },
      { params: { customerId: this.customerId } }
    );
    return response.data;
  };

  unblockRoad = async (roadId: string): Promise<void> => {
    await this.API.delete('dispatch-monitor/hauler-monitor/block-road', {
      params: { customerId: this.customerId, roadId },
    });
  };

  getBlockedRoads = async (): Promise<BlockedRoad[]> => {
    const response = await this.API.get(
      'dispatch-monitor/hauler-monitor/blocked-roads',
      { params: { customerId: this.customerId } }
    );
    return response.data.blockedRoads ?? [];
  };
}
